import { useCallback, useEffect, useMemo, useState } from "react";
import { getCandidatos } from "../Api/ApiCandidato";
import { getLugar } from "../Api/ApiMetodo";
import { guardarCache, leerCache } from "../utils/almacenLocal";
import { esErrorDeRed } from "../utils/colaSincronizacion";

const CLAVE_LUGARES_MESAS = "catalogos:lugares-mesas";
const CLAVE_CANDIDATOS = "catalogos:candidatos";

// opciones de mesa 1..total para SelectComponent (la mesa se guarda como texto)
const opcionesHasta = (total) =>
  Array.from({ length: total }, (_, i) => ({ value: String(i + 1), label: `Mesa ${i + 1}` }));

// Carga los catálogos electorales (candidatos y lugares con su número de mesas)
// desde la BD y los expone también en formato { value, label } para SelectComponent.
// incluirCandidatos: false en pantallas que no los usan (el endpoint de
// candidatos es solo para admin, el digitador recibiría 403).
const useCatalogos = ({ incluirCandidatos = true } = {}) => {
  const [candidatos, setCandidatos] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [cargando, setCargando] = useState(true);

  // Con conexión se guarda una copia en el equipo; sin conexión se usa la
  // última copia para poder seguir registrando (ver utils/almacenLocal).
  const recargar = useCallback(async () => {
    setCargando(true);
    try {
      const [resLugares, resCandidatos] = await Promise.all([
        getLugar(),
        incluirCandidatos ? getCandidatos() : Promise.resolve({ data: [] }),
      ]);
      const catalogos = {
        lugares: resLugares.data ?? [],
        candidatos: resCandidatos.data ?? [],
      };
      setLugares(catalogos.lugares);
      setCandidatos(catalogos.candidatos);
      guardarCache(CLAVE_LUGARES_MESAS, { lugares: catalogos.lugares });
      if (incluirCandidatos) guardarCache(CLAVE_CANDIDATOS, catalogos.candidatos);
    } catch (error) {
      if (!esErrorDeRed(error)) {
        console.error(error);
        return;
      }
      const [copiaLugaresMesas, copiaCandidatos] = await Promise.all([
        leerCache(CLAVE_LUGARES_MESAS),
        incluirCandidatos ? leerCache(CLAVE_CANDIDATOS) : null,
      ]);
      setLugares(copiaLugaresMesas?.lugares ?? []);
      setCandidatos(copiaCandidatos ?? []);
    } finally {
      setCargando(false);
    }
  }, [incluirCandidatos]);

  useEffect(() => {
    recargar();
  }, [recargar]);

  const opcionesCandidato = useMemo(
    () => candidatos.map((c) => ({ value: c.id, label: c.nombre })),
    [candidatos]
  );
  const opcionesLugar = useMemo(
    () => lugares.map((l) => ({ value: l.lugar, label: l.lugar })),
    [lugares]
  );
  // sin lugar elegido (filtros): mesas 1..la mayor cantidad entre todos los lugares
  const opcionesMesa = useMemo(
    () => opcionesHasta(Math.max(0, ...lugares.map((l) => l.numeroMesas ?? 0))),
    [lugares]
  );

  // mesas del lugar elegido (1..numeroMesas); sin lugar (o uno que ya no está
  // en el catálogo, p. ej. en registros antiguos), todas las mesas
  const opcionesMesaDe = useCallback(
    (nombreLugar) => {
      const lugar = nombreLugar && lugares.find((l) => l.lugar === nombreLugar);
      return lugar ? opcionesHasta(lugar.numeroMesas ?? 0) : opcionesMesa;
    },
    [lugares, opcionesMesa]
  );

  return {
    candidatos,
    lugares,
    opcionesCandidato,
    opcionesLugar,
    opcionesMesa,
    opcionesMesaDe,
    cargando,
    recargar,
  };
};

export default useCatalogos;
